/**
 * Localization Popup Web Component
 * Custom element for country and language selection
 */

class LocalizationPopupElement extends HTMLElement {
  constructor() {
    super();
    this.overlay = null;
    this.popup = null;
    this.trigger = null;
    this.closeButton = null;
    this.form = null;
    this.countrySelect = null;
    this.languageSelect = null;
    this.countryInput = null;
    this.languageInput = null;

    // Country to language mapping
    this.countryLanguageMap = {
      US: "en", // United States -> English
      IT: "it", // Italy -> Italian
      FR: "fr", // France -> French
      IS: "is", // Iceland -> Icelandic
    };
  }

  connectedCallback() {
    // Get all elements
    this.overlay = this.querySelector(".localization-popup-overlay");
    this.popup = this.querySelector(".localization-popup");
    this.trigger = this.querySelector(".localization-popup-trigger");
    this.closeButton = this.querySelector(".localization-popup__close");
    this.form = this.querySelector(".localization-popup__form");
    this.countrySelect = this.querySelector("#country-select");
    this.languageSelect = this.querySelector("#language-select");
    this.countryInput = this.querySelector("#country_code");
    this.languageInput = this.querySelector("#locale_code");

    // Determine component type
    this.isPopupContainer = !!this.overlay; // Has popup content
    this.isTriggerContainer = !!this.trigger; // Has trigger button

    if (this.isPopupContainer) {
      // This is the popup container
      this.initPopup();
    } else if (this.isTriggerContainer) {
      // This is the trigger container
      this.initTrigger();
    }
  }

  initTrigger() {
    // Trigger button opens the popup
    this.trigger.addEventListener("click", () => {
      // Find the popup container on the page
      const popupContainer = document.querySelector("localization-popup .localization-popup-overlay")?.closest("localization-popup");
      if (popupContainer && popupContainer.openPopup) {
        popupContainer.openPopup();
      } else {
        console.warn("Localization popup container not found");
      }
    });
  }

  initPopup() {
    // Close button
    if (this.closeButton) {
      this.closeButton.addEventListener("click", () => this.close());
    }

    // Click outside to close
    this.overlay.addEventListener("click", (e) => {
      if (e.target === this.overlay) this.close();
    });

    // ESC key to close
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.overlay.classList.contains("active")) {
        this.close();
      }
    });

    // Initialize selectors
    if (this.countrySelect && this.countryInput) {
      this.initSelector(this.countrySelect, this.countryInput, "country");
    }

    if (this.languageSelect && this.languageInput) {
      this.initSelector(this.languageSelect, this.languageInput, "language");
    }

    // Form submission
    if (this.form) {
      this.form.addEventListener("submit", (e) => this.handleSubmit(e));
    }
  }

  open() {
    this.overlay.classList.add("active");
    document.body.style.overflow = "hidden";

    // Focus first selector
    setTimeout(() => {
      if (this.countrySelect) {
        this.countrySelect.focus();
      }
    }, 100);

    // Dispatch custom event
    this.dispatchEvent(
      new CustomEvent("localization-popup-opened", {
        bubbles: true,
        detail: { popup: this },
      })
    );
  }

  close() {
    this.overlay.classList.remove("active");
    document.body.style.overflow = "";

    // Close all dropdowns
    this.closeAllDropdowns();

    // Dispatch custom event
    this.dispatchEvent(
      new CustomEvent("localization-popup-closed", {
        bubbles: true,
        detail: { popup: this },
      })
    );
  }

  closeAllDropdowns() {
    const allSelects = this.querySelectorAll(".localization-popup__select");
    const allDropdowns = this.querySelectorAll(".localization-popup__dropdown");

    allSelects.forEach((select) => {
      select.setAttribute("aria-expanded", "false");
    });

    allDropdowns.forEach((dropdown) => {
      dropdown.setAttribute("hidden", "");
    });
  }

  initSelector(selectButton, hiddenInput, type) {
    const dropdown = selectButton.nextElementSibling;
    if (!dropdown) return;

    const optionsList = dropdown.querySelector(".localization-popup__options-list");
    const searchInput = dropdown.querySelector(".localization-popup__search");
    const options = optionsList.querySelectorAll(".localization-popup__option");

    // Setup search functionality
    if (searchInput) {
      searchInput.addEventListener("input", (e) => {
        const searchTerm = e.target.value.toLowerCase().trim();
        this.filterOptions(options, searchTerm);
      });

      // Clear search when dropdown closes
      selectButton.addEventListener("click", () => {
        if (!dropdown.hasAttribute("hidden")) {
          searchInput.value = "";
          this.filterOptions(options, "");
        }
      });
    }

    // Toggle dropdown
    selectButton.addEventListener("click", (e) => {
      e.stopPropagation();
      const isExpanded = selectButton.getAttribute("aria-expanded") === "true";

      // Close all other dropdowns first
      this.closeAllDropdowns();

      // Toggle current
      if (!isExpanded) {
        selectButton.setAttribute("aria-expanded", "true");
        dropdown.removeAttribute("hidden");

        // Focus search input if available
        if (searchInput) {
          setTimeout(() => searchInput.focus(), 100);
        }

        // Scroll active option into view
        const activeOption = optionsList.querySelector(".localization-popup__option--active");
        if (activeOption) {
          activeOption.scrollIntoView({ block: "nearest" });
        }
      }
    });

    // Select option
    options.forEach((option) => {
      option.addEventListener("click", (e) => {
        e.stopPropagation();
        const value = option.dataset.value;

        // Update hidden input
        hiddenInput.value = value;

        // Update button display
        this.updateSelectDisplay(selectButton, option, type);

        // Update active state
        options.forEach((opt) => {
          opt.classList.remove("localization-popup__option--active");
          opt.removeAttribute("aria-selected");
        });
        option.classList.add("localization-popup__option--active");
        option.setAttribute("aria-selected", "true");

        // Close dropdown
        selectButton.setAttribute("aria-expanded", "false");
        dropdown.setAttribute("hidden", "");

        // Auto-select language when country is selected
        if (type === "country") {
          this.autoSelectLanguageForCountry(value);
        }

        // Dispatch change event
        this.dispatchEvent(
          new CustomEvent("localization-selection-changed", {
            bubbles: true,
            detail: { type, value, option },
          })
        );
      });
    });

    // Close dropdown when clicking outside
    document.addEventListener("click", (e) => {
      if (!selectButton.contains(e.target) && !dropdown.contains(e.target)) {
        selectButton.setAttribute("aria-expanded", "false");
        dropdown.setAttribute("hidden", "");
      }
    });

    // Keyboard navigation
    selectButton.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        selectButton.click();
      }
    });

    // Keyboard navigation in dropdown
    dropdown.addEventListener("keydown", (e) => {
      const currentOption = dropdown.querySelector(".localization-popup__option--active");
      let nextOption;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          nextOption = currentOption?.nextElementSibling || options[0];
          if (nextOption) nextOption.focus();
          break;
        case "ArrowUp":
          e.preventDefault();
          nextOption = currentOption?.previousElementSibling || options[options.length - 1];
          if (nextOption) nextOption.focus();
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          if (currentOption) currentOption.click();
          break;
        case "Escape":
          e.preventDefault();
          selectButton.setAttribute("aria-expanded", "false");
          dropdown.setAttribute("hidden", "");
          selectButton.focus();
          break;
      }
    });
  }

  updateSelectDisplay(selectButton, option, type) {
    const selectValue = selectButton.querySelector(".localization-popup__select-value");
    if (!selectValue) return;

    // Clone the option content
    const flag = option.querySelector(".localization-popup__flag")?.cloneNode(true);
    const text = option.querySelector(".localization-popup__option-text")?.cloneNode(true);

    selectValue.innerHTML = "";
    if (flag) selectValue.appendChild(flag);
    if (text) {
      const textWrapper = document.createElement("span");
      textWrapper.className = "localization-popup__select-text";
      textWrapper.innerHTML = text.innerHTML;
      selectValue.appendChild(textWrapper);
    }
  }

  autoSelectLanguageForCountry(countryCode) {
    // Check if we have a language mapping for this country
    const languageCode = this.countryLanguageMap[countryCode];

    if (!languageCode) {
      // No mapping found, do nothing
      return;
    }

    // Find the language option with this code
    const languageDropdown = this.languageSelect?.nextElementSibling;
    if (!languageDropdown) return;

    const languageOptions = languageDropdown.querySelectorAll(".localization-popup__option");
    let targetOption = null;

    // Find the option that matches the language code
    languageOptions.forEach((option) => {
      const optionValue = option.dataset.value;
      if (optionValue === languageCode) {
        targetOption = option;
      }
    });

    if (targetOption) {
      // Update the hidden input
      if (this.languageInput) {
        this.languageInput.value = languageCode;
      }

      // Update the display
      this.updateSelectDisplay(this.languageSelect, targetOption, "language");

      // Update active state
      languageOptions.forEach((opt) => {
        opt.classList.remove("localization-popup__option--active");
        opt.removeAttribute("aria-selected");
      });
      targetOption.classList.add("localization-popup__option--active");
      targetOption.setAttribute("aria-selected", "true");

      // Dispatch change event for language
      this.dispatchEvent(
        new CustomEvent("localization-selection-changed", {
          bubbles: true,
          detail: { type: "language", value: languageCode, option: targetOption, autoSelected: true },
        })
      );
    }
  }

  handleSubmit(e) {
    e.preventDefault();

    const countryCode = this.countryInput?.value;
    const localeCode = this.languageInput?.value;

    if (!countryCode || !localeCode) {
      console.error("Localization popup: Missing country or language selection");
      return;
    }

    // Dispatch submit event (can be prevented)
    const submitEvent = new CustomEvent("localization-popup-submit", {
      bubbles: true,
      cancelable: true,
      detail: { countryCode, localeCode },
    });

    if (this.dispatchEvent(submitEvent)) {
      // If not prevented, submit the form
      this.form.submit();
    }
  }

  // Filter options based on search term
  filterOptions(options, searchTerm) {
    let visibleCount = 0;

    options.forEach((option) => {
      const searchData = option.getAttribute("data-search") || "";
      const matches = searchData.includes(searchTerm);

      if (matches) {
        option.classList.remove("hidden");
        visibleCount++;
      } else {
        option.classList.add("hidden");
      }
    });

    // Update the parent list for empty state styling
    const optionsList = options[0]?.parentElement;
    if (optionsList) {
      if (visibleCount === 0) {
        optionsList.classList.add("empty");
      } else {
        optionsList.classList.remove("empty");
      }
    }
  }

  // Public API
  openPopup() {
    this.open();
  }

  closePopup() {
    this.close();
  }

  getSelectedCountry() {
    return this.countryInput?.value;
  }

  getSelectedLanguage() {
    return this.languageInput?.value;
  }
}

// Define custom element
if (!customElements.get("localization-popup")) {
  customElements.define("localization-popup", LocalizationPopupElement);
}

// Export for potential use in other scripts
if (typeof window !== "undefined") {
  window.LocalizationPopupElement = LocalizationPopupElement;
}
